// Shared MFA challenge page (/mfa). Mounted by every PeptideClients SPA
// outside its `<RequireMfaVerified />` guard so the user has a place to
// land when the gate redirects them. Self-contained — uses `@pc/db`
// directly for session + sign-out + OTP / recovery RPCs, so the only
// per-app concern is to pick the post-verify landing route.
//
// Flow:
//
//   1. On mount, calls `user-sms-auth` `mfa_login_start` -- the server
//      texts a 6-digit code to the user's verified phone (the same
//      phone that was on file when 2FA was enabled).
//
//   2. Shows the OTP input. On submit, calls `mfa_login_verify` with
//      the code + the session_id from the JWT.
//
//   3. On success, refreshes the access token (so the next claim read
//      sees `mfa_verified_at`) and navigates to the configured
//      post-verify route. The default is `/` because that's the right
//      home for all four authenticated SPAs (operator dashboard,
//      vendor dashboard, admin dashboard, partners dashboard) — the
//      shop SPA passes `'/'` explicitly via the redirect-back
//      mechanism since shop has no signed-in homepage of its own.
//
//   4. "Use a recovery code instead" swaps the OTP input for a
//      recovery-code input and calls `recovery_use` -- the same RPC
//      used after a lost phone. The user's 10 codes were issued when
//      they enabled 2FA (and can be regenerated from Settings →
//      Security in the operator app).

import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  getMySmsPreferences,
  readMfaClaims,
  redeemRecoveryCode,
  refreshAccessToken,
  signOut as dbSignOut,
  startMfaLogin,
  verifyMfaLogin,
} from '@pc/db';
import { describeError } from '@pc/core';
import { Button } from '../Button';
import { Card } from '../Card';
import { Display } from '../Display';
import { Field } from '../Field';
import { Input } from '../Input';
import { SmallCap } from '../SmallCap';
import { readMfaLocalHint, writeMfaLocalHint } from './mfaLocalHint';

type Mode = 'otp' | 'recovery';
const RESEND_COOLDOWN_SECONDS = 60;
const RESEND_HOURLY_LOCK_SECONDS = 60 * 60;
const MFA_RETURN_TO_STORAGE_KEY = 'pc.mfa.return_to';

function parseRetryAfterSeconds(message: string): number | null {
  const m = message.match(/try again in\s+(\d+)\s+seconds?/i);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function formatCountdownHms(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const seconds = safe % 60;
  return [hours, minutes, seconds].map((part) => String(part).padStart(2, '0')).join(':');
}

function isBlockedPostVerifyPath(path: string): boolean {
  return path === '/mfa' ||
    path.startsWith('/mfa?') ||
    path === '/sign-in' ||
    path.startsWith('/sign-in?') ||
    path === '/auth/callback' ||
    path.startsWith('/auth/callback?');
}

function safePostVerifyPath(raw: string | null | undefined, fallback: string): string {
  if (!raw || typeof raw !== 'string') return fallback;
  if (!raw.startsWith('/') || raw.startsWith('//')) return fallback;
  if (isBlockedPostVerifyPath(raw)) return fallback;
  return raw;
}

interface MfaChallengePageProps {
  /**
   * Where to send the user after a successful verify. Defaults to `/`.
   * Per-SPA overrides exist for surfaces that mount their dashboard
   * on a non-root path. The `from.pathname` carried in
   * `location.state` (set by RequireMfaVerified when it redirects)
   * takes precedence so the user round-trips back to whatever they
   * were trying to reach.
   */
  postVerifyPath?: string;
  /**
   * Optional callback for SPAs that need to clear per-app state
   * (vendor context, active org, etc.) before / after the sign-out
   * triggered by the "Sign out" affordance in the footer.
   */
  onSignOut?: () => Promise<void> | void;
}

export function MfaChallengePage({
  postVerifyPath = '/',
  onSignOut,
}: MfaChallengePageProps = {}) {
  const nav = useNavigate();
  const location = useLocation();

  const [mode, setMode] = useState<Mode>('otp');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [challengeFactor, setChallengeFactor] = useState<'sms' | 'email'>('sms');
  const [error, setError] = useState<string | null>(null);
  const [sentAt, setSentAt] = useState<Date | null>(null);
  const [resendUntil, setResendUntil] = useState<number | null>(null);
  const [resendLockUntil, setResendLockUntil] = useState<number | null>(null);
  const [clockNow, setClockNow] = useState<number>(() => Date.now());

  const resendCountdownSeconds = resendUntil
    ? Math.max(0, Math.ceil((resendUntil - clockNow) / 1000))
    : 0;
  const lockCountdownSeconds = resendLockUntil
    ? Math.max(0, Math.ceil((resendLockUntil - clockNow) / 1000))
    : 0;

  function challengeStorageKey(factor: 'sms' | 'email'): string {
    return `pc.mfa.challenge.sentAt:${factor}`;
  }

  function markChallengeSentNow(factor: 'sms' | 'email') {
    const now = Date.now();
    setSentAt(new Date(now));
    setResendUntil(now + RESEND_COOLDOWN_SECONDS * 1000);
    try {
      window.sessionStorage.setItem(challengeStorageKey(factor), String(now));
    } catch {
      // ignore
    }
  }

  useEffect(() => {
    const id = window.setInterval(() => setClockNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  // On mount: send the initial OTP. A `sessionStorage` sentinel claimed
  // SYNCHRONOUSLY before the send (see the `claimedAt` write below)
  // dedupes a concurrent second run of this effect -- React 18
  // strict-mode double-invoke in dev, or a quick unmount -> remount in
  // prod -- so only one OTP/SMS goes out per login. The server's
  // 5-per-hour cap is a backstop, not the primary guard.
  useEffect(() => {
    let cancelled = false;
    async function bootstrap() {
      setSending(true);
      try {
        // Guard against texting a code when this session is ALREADY
        // MFA-verified. A background token refresh (~hourly) or a stray
        // route remount can re-run this effect on a verified session; the
        // resend sentinel below only covers a 10-minute window, so an
        // hourly refresh sailed past it and shipped the user an
        // unsolicited OTP every ~hour from an idle open tab (2026-06-03,
        // operator getting codes without logging in). If the server says
        // we're verified (or the same-origin local hint does), skip the
        // send and leave /mfa instead of challenging again.
        try {
          const claims = await readMfaClaims();
          const alreadyVerified = Boolean(
            claims?.verified_at || (claims && readMfaLocalHint(claims.auth_session_id)),
          );
          if (alreadyVerified) {
            if (!cancelled) {
              const fromState = (location.state as { from?: { pathname?: string; search?: string; hash?: string } } | null)?.from;
              const statePath = fromState
                ? `${fromState.pathname ?? ''}${fromState.search ?? ''}${fromState.hash ?? ''}`
                : null;
              let storedPath: string | null = null;
              try {
                storedPath = window.sessionStorage.getItem(MFA_RETURN_TO_STORAGE_KEY);
              } catch {
                // ignore storage failures
              }
              nav(safePostVerifyPath(statePath ?? storedPath, postVerifyPath), { replace: true });
            }
            return;
          }
        } catch {
          // Could not determine verification state -- fall through and
          // send the code as before (fail toward challenging the user).
        }
        const prefs = await getMySmsPreferences().catch(() => null);
        const factor = prefs?.mfa_factor === 'email' ? 'email' : 'sms';
        if (!cancelled) setChallengeFactor(factor);
        let reused = false;
        let claimedAt: number | null = null;
        try {
          const seenAtRaw = window.sessionStorage.getItem(challengeStorageKey(factor));
          const seenAt = seenAtRaw ? Number(seenAtRaw) : Number.NaN;
          if (Number.isFinite(seenAt) && Date.now() - seenAt < 10 * 60 * 1000) {
            if (!cancelled) {
              setSentAt(new Date(seenAt));
              setResendUntil(seenAt + RESEND_COOLDOWN_SECONDS * 1000);
            }
            reused = true;
          } else {
            // Claim the send slot SYNCHRONOUSLY -- write the sentinel
            // BEFORE the network round-trip below, with no await between
            // the read above and this write. A concurrent second run of
            // this effect (React 18 strict-mode double-invoke in dev, or
            // a quick unmount -> remount in prod when RequireMfaVerified
            // re-evaluates on an onAuthStateChange tick) then reads this
            // sentinel and reuses it instead of firing a second OTP.
            // Writing it only AFTER `await startMfaLogin` left a
            // time-of-check/time-of-use window that shipped two login
            // SMS ~55ms apart. (2026-06-03)
            claimedAt = Date.now();
            window.sessionStorage.setItem(challengeStorageKey(factor), String(claimedAt));
          }
        } catch {
          // ignore storage errors and send fresh code
        }
        if (!reused) {
          try {
            await startMfaLogin({ factor });
            const sentMoment = claimedAt ?? Date.now();
            if (!cancelled) {
              setSentAt(new Date(sentMoment));
              setResendUntil(sentMoment + RESEND_COOLDOWN_SECONDS * 1000);
            }
          } catch (err) {
            // Roll back the optimistic claim so a genuine send failure
            // (network blip / transient 5xx) can retry on the next mount
            // instead of being suppressed by our own sentinel.
            try {
              window.sessionStorage.removeItem(challengeStorageKey(factor));
            } catch {
              // ignore storage failures
            }
            throw err;
          }
        }
      } catch (err) {
        if (!cancelled) {
          const msg = describeError(err);
          if (/rate limit|too many mfa codes|an hour/i.test(msg)) {
            const retryAfter = parseRetryAfterSeconds(msg) ?? RESEND_HOURLY_LOCK_SECONDS;
            setResendLockUntil(Date.now() + retryAfter * 1000);
          }
          setError(msg);
        }
      } finally {
        if (!cancelled) setSending(false);
      }
    }
    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, []);

  async function onResend() {
    if (resendCountdownSeconds > 0 || lockCountdownSeconds > 0) return;
    setSending(true);
    setError(null);
    try {
      await startMfaLogin({ factor: challengeFactor });
      markChallengeSentNow(challengeFactor);
    } catch (err) {
      const msg = describeError(err);
      if (/rate limit|too many mfa codes|an hour/i.test(msg)) {
        const retryAfter = parseRetryAfterSeconds(msg) ?? RESEND_HOURLY_LOCK_SECONDS;
        setResendLockUntil(Date.now() + retryAfter * 1000);
      }
      setError(msg);
    } finally {
      setSending(false);
    }
  }

  async function onVerify(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const claims = await readMfaClaims();
      const sessionId = claims?.auth_session_id;
      if (!sessionId) {
        setError(
          'Could not read your session id from the access token. Sign out and back in, then try again.',
        );
        return;
      }
      if (mode === 'otp') {
        await verifyMfaLogin({ code: code.trim(), authSessionId: sessionId, factor: challengeFactor });
      } else {
        await redeemRecoveryCode({ code: code.trim(), authSessionId: sessionId });
      }
      // Local hint FIRST, before the token refresh kicks off. The
      // hint is keyed by the JWT session_id (stable across refreshes),
      // so writing it now means the very next `RequireMfaVerified`
      // tick after we nav away allows immediately, even if the
      // refreshed token hasn't propagated through every effect yet.
      writeMfaLocalHint(sessionId);
      // Force a token refresh so the next claim read sees mfa_verified_at.
      await refreshAccessToken();
      // Honor the "where the user was trying to go" state when the
      // gate redirected them here; fall back to the per-SPA default.
      const fromState = (location.state as { from?: { pathname?: string; search?: string; hash?: string } } | null)?.from;
      const statePath = fromState
        ? `${fromState.pathname ?? ''}${fromState.search ?? ''}${fromState.hash ?? ''}`
        : null;
      let storedPath: string | null = null;
      try {
        storedPath = window.sessionStorage.getItem(MFA_RETURN_TO_STORAGE_KEY);
      } catch {
        // ignore storage failures
      }
      const nextPath = safePostVerifyPath(statePath ?? storedPath, postVerifyPath);
      try {
        window.sessionStorage.removeItem(MFA_RETURN_TO_STORAGE_KEY);
      } catch {
        // ignore storage failures
      }
      nav(nextPath, { replace: true });
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleSignOut() {
    try {
      if (onSignOut) {
        await onSignOut();
      } else {
        await dbSignOut();
      }
    } catch {
      // Best-effort; if sign-out throws we still want to land the user
      // back at /sign-in via supabase-js's auth-state listener.
    }
    nav('/sign-in', { replace: true });
  }

  return (
    <main className="min-h-screen grid place-items-center bg-paper px-4">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <SmallCap>Security check</SmallCap>
          <Display size="md" className="mt-2">
            Two-factor <em>code</em>
          </Display>
        </div>

        {error ? (
          <div
            role="alert"
            className="mb-4 bg-linen border border-rule border-l-4 border-l-clay px-4 py-3"
          >
            <p className="text-sm text-ink-2 leading-relaxed">{error}</p>
          </div>
        ) : null}

        <Card>
          <form onSubmit={onVerify} className="flex flex-col gap-4">
            {mode === 'otp' ? (
              <>
                <p className="text-sm text-ink-2">
                  We just {challengeFactor === 'email' ? 'emailed' : 'texted'} a 6-digit code. Enter it
                  below to finish signing in.
                  {sentAt ? (
                    <>
                      {' '}
                      <span className="text-xs text-muted">
                        Sent {sentAt.toLocaleTimeString()}.
                      </span>
                    </>
                  ) : null}
                </p>
                <Field label="Code" htmlFor="mfa-otp">
                  <Input
                    id="mfa-otp"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    autoComplete="one-time-code"
                    required
                    autoFocus
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                  />
                </Field>
              </>
            ) : (
              <>
                <p className="text-sm text-ink-2">
                  Enter one of your 10-character recovery codes. Each code works
                  once. If you don’t have your codes, sign out and contact
                  support.
                </p>
                <Field label="Recovery code" htmlFor="mfa-rec">
                  <Input
                    id="mfa-rec"
                    autoComplete="one-time-code"
                    required
                    autoFocus
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    placeholder="e.g. K3QM7P9XCB"
                  />
                </Field>
              </>
            )}
            <Button
              type="submit"
              disabled={busy || code.length < (mode === 'otp' ? 6 : 8)}
            >
              {busy ? 'Verifying...' : 'Verify and continue'}
            </Button>
          </form>

          <div className="mt-6 flex flex-col gap-3 text-center text-xs text-muted">
            {mode === 'otp' ? (
              <>
                <button
                  type="button"
                  className="underline hover:text-ink disabled:opacity-50"
                  disabled={sending || resendCountdownSeconds > 0 || lockCountdownSeconds > 0}
                  onClick={() => void onResend()}
                >
                  {lockCountdownSeconds > 0
                    ? `Resend locked (${formatCountdownHms(lockCountdownSeconds)})`
                    : resendCountdownSeconds > 0
                      ? `Resend code in ${formatCountdownHms(resendCountdownSeconds)}`
                      : sending
                        ? 'Sending...'
                        : 'Resend code'}
                </button>
                <button
                  type="button"
                  className="underline hover:text-ink"
                  onClick={() => {
                    setMode('recovery');
                    setCode('');
                    setError(null);
                  }}
                >
                  Use a recovery code instead
                </button>
              </>
            ) : (
              <button
                type="button"
                className="underline hover:text-ink"
                onClick={() => {
                  setMode('otp');
                  setCode('');
                  setError(null);
                }}
              >
                Back to {challengeFactor === 'email' ? 'email code' : 'SMS code'}
              </button>
            )}
            <button
              type="button"
              className="underline hover:text-ink"
              onClick={() => void handleSignOut()}
            >
              Sign out
            </button>
          </div>
        </Card>
      </div>
    </main>
  );
}
